import { BannerCard, BannerTitle } from "@/components/ui/banners";
import { seo } from "@/lib/seo";
import { cn } from "@bidbattles/bloks/lib/ui";
import {
	type IconDefinition,
	faBan,
	faBoxOpen,
	faBuilding,
	faBullseye,
	faCalculator,
	faCertificate,
	faCheckCircle,
	faClock,
	faCommentDots,
	faCreditCard,
	faGraduationCap,
	faHeadset,
	faHeart,
	faLock,
	faMoneyBill,
	faQuoteLeft,
	faQuoteRight,
	faScaleBalanced,
	faShieldHalved,
	faStar,
	faTruckFast,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/about")({
	component: RouteComponent,
	head: () => ({
		meta: [
			...seo({
				title: "About BidBattles | BidBattles",
				description:
					"Learn how BidBattles makes luxury products accessible through fair, capped-player auctions.",
			}),
		],
	}),
});

const heroStats = [
	{ value: "10,000+", label: "Happy Winners" },
	{ value: "€5M+", label: "Products Won" },
	{ value: "98%", label: "Satisfaction Rate" },
] as const;

const journeyItems = [
	{
		period: "Q1 2025",
		title: "The Spark",
		description:
			"Founded in Amsterdam with a mission to democratize luxury auctions",
	},
	{
		period: "Q2 2025",
		title: "Building the concept",
		description: "Launched our first capped-player test auction",
	},
	{
		period: "Q3 2025",
		title: "1st Beta Test Launch",
		description:
			"Introduced Premium Battles featuring luxury vehicles and high-value items",
	},
	{
		period: "Q4 2025",
		title: "1st Beta Launch BidBattles",
		description: "Testing the capacity of our platform to full extend",
	},
	{
		period: "Today",
		title: "Leading the Way",
		description:
			"The Netherlands' first and most exciting capped-player auction platform",
	},
] as const;

const differentiators = [
	{
		icon: faBullseye,
		value: "200",
		label: "Maximum Players per Battle",
		title: "Limited Seats Create Fair Competition",
		paragraphs: [
			"Unlike traditional auctions where you compete against unlimited bidders, our battles cap the number of participants. When a battle fills up, it's closed, meaning you only face a defined number of opponents.",
			"This revolutionary approach ensures everyone has a real chance to win, not just those with infinite resources.",
		],
	},
	{
		icon: faScaleBalanced,
		value: "Equal",
		label: "Playing Field for All",
		title: "No Pay-to-Win Mechanics",
		paragraphs: [
			"Every player pays the same entry price. Every player has access to the same bid packages. No VIP advantages, no premium shortcuts, no hidden power-ups.",
			<>
				Success comes down to <strong>strategy and timing</strong>, not the size
				of your wallet.
			</>,
		],
	},
	{
		icon: faGraduationCap,
		value: "3",
		label: "Bid Types for Strategy",
		title: "Strategic Bidding System",
		paragraphs: [
			"Choose between instant bids, balanced bids, or time-extension bids. Each has different costs and effects, allowing for diverse strategies.",
		],
		bids: [
			{
				label: "+0s Bid (€30.00)",
				description: "Instant pressure, no time added",
			},
			{
				label: "+3s Bid (€10.00)",
				description: "Premium Battle bid, only 3 seconds added",
			},
			{
				label: "+5s Bid (€6.00)",
				description: "Premium Battle time extension, 5 seconds added",
			},
		],
	},
] satisfies ReadonlyArray<{
	icon: IconDefinition;
	value: string;
	label: string;
	title: string;
	paragraphs: readonly React.ReactNode[];
	bids?: ReadonlyArray<{ label: string; description: string }>;
}>;

const authenticityItems = [
	{
		icon: faBoxOpen,
		title: "Brand New",
		description: "All products are factory sealed and unused",
	},
	{
		icon: faShieldHalved,
		title: "Full Warranty",
		description: "Legal manufacturer warranties apply",
	},
	{
		icon: faCertificate,
		title: "Certified",
		description: "Sourced from authorized distributors only",
	},
] as const;

const qualityPromises = [
	{
		title: "Direct from manufacturers",
		description:
			"We work exclusively with authorized distributors and official retail partners.",
	},
	{
		title: "Legal warranty coverage",
		description:
			"All products include full manufacturer warranty as required by Dutch law (wettelijke garantievoorwaarden).",
	},
	{
		title: "Secure delivery",
		description:
			"Fully insured shipping to the Netherlands and Belgium with tracking.",
	},
] as const;

const responsibleGamingItems = [
	{
		icon: faMoneyBill,
		title: "Set Your Budget",
		description:
			"Decide your maximum spend before entering any battle. Never bid more than you can afford to lose.",
	},
	{
		icon: faClock,
		title: "Take Breaks",
		description:
			"Set time limits for your sessions. It's easy to get caught up in the excitement, so step away regularly.",
	},
	{
		icon: faCalculator,
		title: "Know Your Costs",
		description:
			"Entry price + bids = total investment. Calculate your potential spend before entering battles.",
	},
	{
		icon: faBan,
		title: "Self-Exclusion Available",
		description:
			"Need a break? Contact support to temporarily or permanently suspend your account.",
	},
] as const;

const trustStats = [
	{
		icon: faStar,
		value: "4.8/5",
		label: "Average Rating",
		detail: "From 2,500+ reviews",
	},
	{
		icon: faTruckFast,
		value: "99.2%",
		label: "On-Time Delivery",
		detail: "Fully tracked & insured",
	},
	{
		icon: faCommentDots,
		value: "<2h",
		label: "Support Response",
		detail: "Mon-Fri, 9am-6pm CET",
	},
	{
		icon: faLock,
		value: "100%",
		label: "Secure Payments",
		detail: "SSL encrypted",
	},
] as const;

const trustBadges = [
	{
		icon: faShieldHalved,
		title: "SSL Secured",
		description: "256-bit encryption",
	},
	{
		icon: faCreditCard,
		title: "iDEAL Payments",
		description: "Dutch banking partner",
	},
	{
		icon: faBuilding,
		title: "KVK Registered",
		description: "Legal Dutch business",
	},
	{
		icon: faHeadset,
		title: "24/7 Support",
		description: "Always here to help",
	},
] as const;

function RouteComponent() {
	return (
		<main className="flex grow flex-col">
			<section className="px-box py-20 md:pb-20 md:pt-30">
				<PageContainer>
					<div className="mx-auto flex max-w-4xl flex-col items-center gap-6 text-center">
						<div className="flex flex-col gap-3">
							<h1 className="text-5xl leading-none font-bold text-gray-900 md:text-6xl">
								About BidBattles
							</h1>
							<p className="text-lg leading-7 text-gray-500 md:text-2xl">
								Making luxury accessible through fair, capped-player auctions
							</p>
						</div>
						<div className="flex w-full flex-col justify-center gap-4 md:gap-8 sm:flex-row">
							{heroStats.map((stat) => (
								<StatCard
									key={stat.label}
									{...stat}
									className="sm:min-w-42"
									emphasis="neutral"
								/>
							))}
						</div>
					</div>
				</PageContainer>
			</section>

			<section className="bg-gray-warm-100 px-box py-12 md:py-20">
				<PageContainer>
					<div className="mx-auto flex max-w-5xl flex-col gap-8 rounded-2xl bg-white p-6 md:p-10 md:py-12">
						<SectionHeading title="Our Story" />
						<blockquote className="rounded-2xl bg-white px-4 py-6 text-lg leading-8 text-gray-700 md:px-8 md:py-8 md:text-xl">
							<FontAwesomeIcon
								aria-hidden
								icon={faQuoteLeft}
								className="me-2 text-accent-primary"
							/>
							We believe luxury shouldn't be reserved for the few. Everyone
							deserves a fair shot at winning their dream products.
							<FontAwesomeIcon
								aria-hidden
								icon={faQuoteRight}
								className="ms-2 text-accent-primary"
							/>
						</blockquote>
						<div className="flex flex-col gap-6 pt-2 text-lg leading-[1.625] text-gray-500">
							<p>
								BidBattles was born from a simple frustration: traditional
								auctions are dominated by those with the deepest pockets. We
								watched as everyday people were consistently outbid by wealthy
								collectors and professional bidders with unlimited budgets.
							</p>
							<p>
								That's when we had our breakthrough idea:{" "}
								<strong className="text-gray-900">
									What if we limited the number of players in each auction?
								</strong>{" "}
								By creating{" "}
								<strong className="text-gray-900">capped-player battles</strong>
								, we level the playing field. Whether you're competing for a
								Tesla Model S or the latest iPhone, you'll only face a limited
								number of opponents, not an endless army of bidders.
							</p>
							<p>
								Founded in 2023 in the Netherlands, BidBattles has quickly
								become the go-to platform for fair, exciting auctions. We've
								helped thousands of people win everything from premium
								electronics to luxury vehicles, all at a fraction of retail
								price.
							</p>
						</div>
						<div className="flex flex-col gap-10 pt-6">
							<h3 className="text-2xl font-bold text-gray-900">Our Journey</h3>
							<ol className="flex flex-col gap-10">
								{journeyItems.map((item, index) => (
									<TimelineItem
										key={item.period}
										{...item}
										isLast={index === journeyItems.length - 1}
									/>
								))}
							</ol>
						</div>
					</div>
				</PageContainer>
			</section>

			<section className="bg-gray-warm-100 px-box py-12 md:py-20">
				<PageContainer>
					<div className="mx-auto flex max-w-5xl flex-col gap-16 rounded-2xl bg-white p-6 md:p-12 md:py-12">
						<SectionHeading
							title="Why BidBattles is Different"
							subtitle="We've revolutionized online auctions with fairness at the core"
						/>
						<div className="flex flex-col gap-12">
							{differentiators.map((item, index) => (
								<article
									key={item.title}
									className={cn(
										"grid items-center gap-8",
										index % 2 === 1
											? "md:grid-cols-[minmax(0,1fr)_25rem]"
											: "md:grid-cols-[25rem_minmax(0,1fr)]",
									)}
								>
									<DifferentiatorVisualCard
										className={index % 2 === 1 ? "md:order-2" : undefined}
										icon={item.icon}
										value={item.value}
										label={item.label}
									/>
									<div className="flex flex-col gap-4">
										<h3 className="text-2xl leading-9 font-bold text-gray-900 md:text-3xl">
											{item.title}
										</h3>
										{item.paragraphs.map((paragraph, index) => (
											<p
												key={index}
												className="text-lg leading-[1.625] text-gray-500"
											>
												{paragraph}
											</p>
										))}
										{item.bids && (
											<ul className="flex flex-col gap-2 text-gray-700">
												{item.bids.map((bid) => (
													<li key={bid.label} className="flex gap-3">
														<span
															aria-hidden
															className="mt-2 size-2 shrink-0 rounded-full bg-accent-primary"
														/>
														<span>
															<strong>{bid.label}</strong> - {bid.description}
														</span>
													</li>
												))}
											</ul>
										)}
									</div>
								</article>
							))}
						</div>
					</div>
				</PageContainer>
			</section>

			<section className="bg-gray-warm-100 px-box py-12 md:py-20">
				<PageContainer>
					<div className="mx-auto flex max-w-4xl flex-col gap-12">
						<SectionHeading
							title="100% Authentic Products"
							subtitle="Every product comes with full warranties and guarantees"
						/>
						<div className="grid gap-6 md:grid-cols-3">
							{authenticityItems.map((item) => (
								<article
									key={item.title}
									className="flex min-h-51 flex-col items-center justify-center gap-3 rounded-2xl border-2 border-gray-200 bg-white p-6 text-center"
								>
									<FontAwesomeIcon
										aria-hidden
										icon={item.icon}
										className="text-3xl text-gray-900"
									/>
									<h3 className="text-lg font-bold text-gray-900">
										{item.title}
									</h3>
									<p className="text-sm text-gray-500">{item.description}</p>
								</article>
							))}
						</div>
						<div className="flex flex-col gap-6 rounded-2xl bg-white p-6 md:p-8">
							<h3 className="text-2xl font-bold text-gray-900">
								Our Quality Promise
							</h3>
							<ul className="flex flex-col gap-4">
								{qualityPromises.map((promise) => (
									<li key={promise.title} className="flex items-start gap-3">
										<FontAwesomeIcon
											aria-hidden
											icon={faCheckCircle}
											className="mt-1 text-accent-primary"
										/>
										<p className="text-gray-500">
											<strong className="text-gray-900">
												{promise.title}:
											</strong>{" "}
											{promise.description}
										</p>
									</li>
								))}
							</ul>
						</div>
					</div>
				</PageContainer>
			</section>

			<section className="px-box py-12 md:py-20">
				<PageContainer>
					<div className="mx-auto flex max-w-4xl flex-col gap-10">
						<SectionHeading
							title="Our Commitment to Responsible Gaming"
							subtitle="Fun and excitement should always come with responsibility"
						/>
						<BannerCard className="gap-4 text-gray-700 md:p-8">
							<BannerTitle className="text-gray-900">
								<FontAwesomeIcon
									aria-hidden
									icon={faHeart}
									className="text-accent-primary"
								/>
								BidBattles is Entertainment, Not Gambling
							</BannerTitle>
							<p>
								While our auctions are thrilling and competitive, we want to be
								clear: BidBattles is a skill-based auction platform, not
								gambling. Success depends on strategic timing and
								decision-making, not chance. However, we recognize that
								competitive bidding can be exciting, and we encourage all
								players to participate responsibly.
							</p>
						</BannerCard>
						<div className="grid gap-6 md:grid-cols-2">
							{responsibleGamingItems.map((item) => (
								<article
									key={item.title}
									className="flex min-h-31 flex-col gap-3 rounded-xl bg-gray-50 p-6"
								>
									<h3 className="flex items-center gap-2 font-bold text-gray-900">
										<FontAwesomeIcon
											aria-hidden
											icon={item.icon}
											className="text-accent-primary"
										/>
										{item.title}
									</h3>
									<p className="text-sm text-gray-500">{item.description}</p>
								</article>
							))}
						</div>
					</div>
				</PageContainer>
			</section>

			<section className="bg-gray-warm-100 px-box py-12 md:py-20">
				<PageContainer>
					<div className="mx-auto flex max-w-6xl flex-col gap-10">
						<SectionHeading
							title="Trusted by Thousands"
							subtitle="What makes BidBattles the most trusted auction platform in the Netherlands"
						/>
						<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
							{trustStats.map((stat) => (
								<StatCard
									key={stat.label}
									{...stat}
									className="min-h-50 justify-center border-transparent"
								/>
							))}
						</div>
						<div className="flex flex-wrap justify-center gap-6">
							{trustBadges.map((badge) => (
								<TrustBadge key={badge.title} {...badge} />
							))}
						</div>
					</div>
				</PageContainer>
			</section>
		</main>
	);
}

function PageContainer({ children }: React.PropsWithChildren) {
	return <div className="mx-auto w-full max-w-container">{children}</div>;
}

function SectionHeading({
	title,
	subtitle,
}: {
	title: string;
	subtitle?: string;
}) {
	return (
		<div className="flex flex-col gap-3 text-center">
			<h2 className="text-3xl font-bold text-gray-900 md:text-4xl">{title}</h2>
			{subtitle && (
				<p className="text-lg text-gray-500 md:text-xl">{subtitle}</p>
			)}
		</div>
	);
}

function StatCard({
	className,
	icon,
	value,
	label,
	detail,
	emphasis = "accent",
}: {
	className?: string;
	icon?: IconDefinition;
	value: string;
	label: string;
	detail?: string;
	emphasis?: "accent" | "neutral";
}) {
	return (
		<div
			className={cn(
				"flex flex-col items-center gap-2 rounded-2xl border border-gray-200 bg-white p-5 text-center",
				className,
			)}
		>
			{icon && (
				<FontAwesomeIcon
					aria-hidden
					icon={icon}
					className="text-3xl text-gray-900"
				/>
			)}
			<p
				className={cn(
					"text-3xl leading-9 font-bold",
					emphasis === "accent" ? "text-accent-primary" : "text-gray-900",
				)}
			>
				{value}
			</p>
			<p className="text-gray-500">{label}</p>
			{detail && <p className="text-sm text-gray-500">{detail}</p>}
		</div>
	);
}

function TimelineItem({
	period,
	title,
	description,
	isLast,
}: {
	period: string;
	title: string;
	description: string;
	isLast: boolean;
}) {
	return (
		<li className="relative flex gap-6">
			<div className="relative flex w-4 shrink-0 justify-center">
				<span className="z-10 mt-1 size-4 rounded-full bg-accent-primary ring-4 ring-white" />
				{!isLast && (
					<span className="absolute top-5 -bottom-7 w-0.5 bg-accent-primary" />
				)}
			</div>
			<div className="flex flex-col gap-1">
				<p className="text-sm font-semibold text-accent-primary">{period}</p>
				<h4 className="text-xl font-bold text-gray-900">{title}</h4>
				<p className="text-gray-500">{description}</p>
			</div>
		</li>
	);
}

function DifferentiatorVisualCard({
	className,
	icon,
	value,
	label,
}: {
	className?: string;
	icon: IconDefinition;
	value: string;
	label: string;
}) {
	return (
		<div
			className={cn(
				"mx-auto flex h-64 w-full max-w-100 flex-col items-center justify-center gap-2 rounded-3xl border border-gray-300 p-8 text-center",
				className,
			)}
		>
			<FontAwesomeIcon
				aria-hidden
				icon={icon}
				className="text-6xl text-gray-400"
			/>
			<p className="text-5xl font-bold text-accent-primary">{value}</p>
			<p className="text-xl text-gray-700">{label}</p>
		</div>
	);
}

function TrustBadge({
	icon,
	title,
	description,
}: {
	icon: IconDefinition;
	title: string;
	description: string;
}) {
	return (
		<div className="flex w-full items-center gap-3 rounded-xl border-2 border-gray-200 bg-white px-6 py-4 sm:w-auto">
			<FontAwesomeIcon
				aria-hidden
				icon={icon}
				className="text-2xl text-accent-primary"
			/>
			<div>
				<p className="font-bold text-gray-900">{title}</p>
				<p className="text-sm text-gray-500">{description}</p>
			</div>
		</div>
	);
}
